import { useState, useEffect } from "react";
import { api } from "../App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Calendar, TrendingUp, Book, Languages, PlayCircle, Heart } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

const translations = {
  fr: {
    title: "Lecture Biblique en 365 Jours",
    subtitle: "Lisez toute la Bible en un an avec un plan de lecture guidé",
    currentReading: "Lecture Actuelle",
    notStarted: "Commencez Votre Parcours",
    notStartedDesc: "Cliquez sur le bouton pour commencer votre lecture de la Bible en 365 jours",
    day: "Jour",
    startReading: "Commencer la lecture",
    continueReading: "Continuer la lecture",
    startJourney: "Commencer mon parcours",
    progress: "Progression",
    statistics: "Statistiques",
    totalBooks: "Livres",
    totalVerses: "Versets",
    totalDays: "Jours",
    version: "Version",
    chapters: "chapitres",
    verses: "versets",
    daysCompleted: "jours complétés",
    loading: "Chargement...",
    features: {
      guided: "Plan Guidé",
      guidedDesc: "Suivez un calendrier de lecture structuré sur 365 jours",
      complete: "Bible Complète",
      completeDesc: "Plus de 31 000 versets de l'Ancien et Nouveau Testament",
      progress: "Suivi de Progression",
      progressDesc: "Marquez vos lectures et suivez votre avancement"
    }
  },
  en: {
    title: "365-Day Bible Reading",
    subtitle: "Read the entire Bible in one year with a guided reading plan",
    currentReading: "Current Reading",
    notStarted: "Start Your Journey",
    notStartedDesc: "Click the button to begin your 365-day Bible reading journey",
    day: "Day",
    startReading: "Start Reading",
    continueReading: "Continue Reading",
    startJourney: "Start My Journey",
    progress: "Progress",
    statistics: "Statistics",
    totalBooks: "Books",
    totalVerses: "Verses",
    totalDays: "Days",
    version: "Version",
    chapters: "chapters",
    verses: "verses",
    daysCompleted: "days completed",
    loading: "Loading...",
    features: {
      guided: "Guided Plan",
      guidedDesc: "Follow a structured 365-day reading calendar",
      complete: "Complete Bible",
      completeDesc: "Over 31,000 verses from Old and New Testament",
      progress: "Progress Tracking",
      progressDesc: "Mark your readings and track your progress"
    }
  },
  es: {
    title: "Lectura Bíblica en 365 Días",
    subtitle: "Lee toda la Biblia en un año con un plan de lectura guiado",
    currentReading: "Lectura Actual",
    notStarted: "Comienza Tu Viaje",
    notStartedDesc: "Haz clic en el botón para comenzar tu viaje de lectura de la Biblia de 365 días",
    day: "Día",
    startReading: "Comenzar lectura",
    continueReading: "Continuar lectura",
    startJourney: "Comenzar mi viaje",
    progress: "Progreso",
    statistics: "Estadísticas",
    totalBooks: "Libros",
    totalVerses: "Versículos",
    totalDays: "Días",
    version: "Versión",
    chapters: "capítulos",
    verses: "versículos",
    daysCompleted: "días completados",
    loading: "Cargando...",
    features: {
      guided: "Plan Guiado",
      guidedDesc: "Sigue un calendario de lectura estructurado de 365 días",
      complete: "Biblia Completa",
      completeDesc: "Más de 31,000 versículos del Antiguo y Nuevo Testamento",
      progress: "Seguimiento de Progreso",
      progressDesc: "Marca tus lecturas y sigue tu avance"
    }
  }
};

const HomePage = ({ language, changeLanguage }) => {
  const [currentDay, setCurrentDay] = useState(1);
  const [todayReading, setTodayReading] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [completedDays, setCompletedDays] = useState(0);
  const navigate = useNavigate();
  const t = translations[language];

  useEffect(() => {
    initializeReading();
    fetchStats();
  }, [language]);

  const initializeReading = async () => {
    try {
      const startDate = localStorage.getItem('reading_start_date');
      const completedDaysData = JSON.parse(localStorage.getItem('completed_days') || '[]');
      
      setCompletedDays(completedDaysData.length);
      
      if (startDate) {
        setHasStarted(true);
        let nextDay = 1;
        for (let i = 1; i <= 365; i++) {
          if (!completedDaysData.includes(i)) {
            nextDay = i;
            break;
          }
        }
        setCurrentDay(nextDay);
        await fetchDayReading(nextDay);
      } else {
        setHasStarted(false);
        await fetchDayReading(1);
      }
    } catch (error) {
      console.error("Error initializing reading:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDayReading = async (day) => {
    try {
      const response = await api.get(`/day-reading/${day}?language=${language}`);
      setTodayReading(response.data);
    } catch (error) {
      console.error("Error fetching day reading:", error);
      toast.error("Erreur lors du chargement de la lecture");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleStartJourney = () => {
    const startDate = new Date().toISOString();
    localStorage.setItem('reading_start_date', startDate);
    localStorage.setItem('completed_days', JSON.stringify([]));
    setHasStarted(true);
    setCurrentDay(1);
    navigate('/read/1');
    toast.success(language === 'fr' ? 'Parcours commencé ! Bonne lecture !' : 'Journey started! Happy reading!');
  };

  const handleContinueReading = () => {
    navigate(`/read/${currentDay}`);
  };

  const progressPercentage = (completedDays / 365) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-amber-900">{t.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-amber-800" />
              <h1 className="text-2xl font-bold text-amber-900 book-title">Bible 365</h1>
            </div>
            <div className="flex items-center gap-4">
              <Select value={language} onValueChange={changeLanguage}>
                <SelectTrigger data-testid="language-selector" className="w-32">
                  <Languages className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 fade-in">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-900 mb-4 book-title">
            {t.title}
          </h2>
          <p className="text-base sm:text-lg text-amber-800 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Progress Card - Only show if started */}
        {hasStarted && (
          <Card data-testid="progress-card" className="max-w-2xl mx-auto mb-8 glass-effect border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <TrendingUp className="w-5 h-5" />
                {t.progress}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm text-amber-800 mb-2">
                <span>{completedDays} / 365 {t.daysCompleted}</span>
                <span>{progressPercentage.toFixed(1)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-3" />
            </CardContent>
          </Card>
        )}

        {/* Current Reading or Start Card */}
        {todayReading && (
          <Card 
            data-testid="current-reading-card"
            className="max-w-2xl mx-auto mb-8 bg-gradient-to-br from-amber-100 to-amber-50 border-amber-300"
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                {hasStarted ? (
                  <>
                    <Calendar className="w-5 h-5" />
                    {t.currentReading} - {t.day} {currentDay}
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-5 h-5" />
                    {t.notStarted}
                  </>
                )}
              </CardTitle>
              <CardDescription className="text-amber-700">
                {hasStarted ? (
                  todayReading.readings.map((r, idx) => (
                    <span key={idx}>
                      {r.book} {r.chapter}
                      {idx < todayReading.readings.length - 1 ? " • " : ""}
                    </span>
                  ))
                ) : (
                  t.notStartedDesc
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasStarted && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-amber-800">
                    <span className="font-semibold">{todayReading.readings.length}</span> {t.chapters} • 
                    <span className="font-semibold"> {todayReading.total_verses}</span> {t.verses}
                  </div>
                </div>
              )}
              <Button
                data-testid="action-button"
                onClick={hasStarted ? handleContinueReading : handleStartJourney}
                className="w-full btn-primary"
              >
                {hasStarted ? (
                  <>
                    <BookOpen className="w-4 h-4 mr-2" />
                    {t.continueReading}
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-4 h-4 mr-2" />
                    {t.startJourney}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Statistics */}
        {stats && (
          <Card data-testid="stats-card" className="max-w-2xl mx-auto glass-effect border-amber-200">
            <CardHeader>
              <CardTitle className="text-amber-900">{t.statistics}</CardTitle>
              <CardDescription className="text-amber-700">
                {t.version}: {stats.version}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-900">{stats.total_books}</div>
                  <div className="text-sm text-amber-700">{t.totalBooks}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-900">{stats.total_verses.toLocaleString()}</div>
                  <div className="text-sm text-amber-700">{t.totalVerses}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-900">{stats.total_days}</div>
                  <div className="text-sm text-amber-700">{t.totalDays}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          <Card data-testid="feature-guided" className="glass-effect border-amber-200 card-hover">
            <CardHeader>
              <Calendar className="w-12 h-12 text-amber-800 mb-4" />
              <CardTitle className="text-amber-900">{t.features.guided}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-800">{t.features.guidedDesc}</p>
            </CardContent>
          </Card>

          <Card data-testid="feature-complete" className="glass-effect border-amber-200 card-hover">
            <CardHeader>
              <Book className="w-12 h-12 text-amber-800 mb-4" />
              <CardTitle className="text-amber-900">{t.features.complete}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-amber-800">{t.features.completeDesc}</p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
